import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CalendarIcon, DotaTagIcon, CsTagIcon, LolTagIcon } from '@icons';

import { useModals } from '@shared/contexts';

import { GAME_TYPE } from '@types';

import { Button } from '@components/button';
import { Modal } from '@components/modal';

import { ModalName } from '../types';
import * as Styled from './tournament-modal.styled';

const TournamentModal = () => {
	const { t } = useTranslation('main');
	const navigate = useNavigate();
	const { isModalOpen, closeModal, getModalData } = useModals();

	const modalData = getModalData<{ event: any; eventIndex: number }>(ModalName.TOURNAMENT);
	const event = modalData?.event || null;
	const eventIndex = modalData?.eventIndex || null;

	const formatEventDate = (startDate: Date | null, endDate: Date | null): string => {
		if (!startDate || !endDate) return t('no_date');

		const formatDate = (date: Date) => {
			if (!date || isNaN(date.getTime())) return '??.??';
			const day = date.getDate().toString().padStart(2, '0');
			const month = (date.getMonth() + 1).toString().padStart(2, '0');
			return `${day}.${month}`;
		};

		const formattedStartDate = formatDate(startDate);
		const formattedEndDate = formatDate(endDate);

		return startDate.getMonth() === endDate.getMonth()
			? `${formattedStartDate}-${formattedEndDate.split('.')[0]}.${formattedEndDate.split('.')[1]}`
			: `${formattedStartDate}-${formattedEndDate}`;
	};

	const handleGoToTournament = () => {
		if (eventIndex !== null) {
			navigate(`/tournament/${eventIndex}`);
			closeModal();
		}
	};

	const handleCloseModal = () => {
		closeModal();
	};

	if (!event) {
		return null;
	}

	return (
		<Modal 
			isOpen={isModalOpen(ModalName.TOURNAMENT)} 
			closeModal={handleCloseModal}
			hasPadding={false}
			backgroundColor='dark'
		>
			<Styled.Wrapper>
				{event.img && (
					<Styled.ModalImg $backgroundImage={event.img} />
				)}
				
				<Styled.ModalInfo>
					<Styled.ModalTitle>
						{t('tournament')} {event.name}
					</Styled.ModalTitle>

					<Styled.ModalGame>
						{event.gameType === GAME_TYPE.DOTA2 && (
							<Styled.ModalGameItem $gameType={event.gameType}>
								<DotaTagIcon />
								<span>Dota 2</span>
							</Styled.ModalGameItem>
						)}
						{event.gameType === GAME_TYPE.CS2 && (
							<Styled.ModalGameItem $gameType={event.gameType}>
								<CsTagIcon />
								<span>CS 2</span>
							</Styled.ModalGameItem>
						)}
						{event.gameType === GAME_TYPE.LOL && (
							<Styled.ModalGameItem $gameType={event.gameType}>
								<LolTagIcon />
								<span>League of Legends</span>
							</Styled.ModalGameItem>
						)}

						<Styled.ModalCalendar>
							<CalendarIcon />
							<span>
								{formatEventDate(event.startDate, event.endDate)}
							</span>
						</Styled.ModalCalendar>
					</Styled.ModalGame>

					<Styled.ModalDescription
						dangerouslySetInnerHTML={{
							__html: event.description
						}}
					/>

					<Styled.ModalButtons>
						<Button
							color="dark"
							onClick={handleCloseModal}
						>
							{t('close')}
						</Button>
						<Button
							color="primary"
							onClick={handleGoToTournament}
						>
							{t('go_to_tournament')}
						</Button>
					</Styled.ModalButtons>
				</Styled.ModalInfo>
			</Styled.Wrapper>
		</Modal>
	);
};

export { TournamentModal };
